<template>
    <section class="accordion-section">
        <div class="accordion-section__container">
            <CardTitle v-if="!hideTitle && item.mainTitle" :module="item" />

            <!-- 桌面端横向手风琴 -->
            <div class="accordion-panels accordion-panels--horizontal">
                <div v-for="(cell, idx) in props.item.fieldData"
                    :key="'h-' + String((cell as Record<string, unknown>)?.id ?? idx)" class="accordion-panel"
                    :class="{ 'accordion-panel--active': activeIndex === idx }" @mouseenter="handleDesktopEnter(idx)"
                    @mouseleave="handleDesktopLeave" @click="handleCellClick(cell)">
                    <img v-if="getCellImage(cell)" :src="getCellImage(cell)" :alt="getCellTitle(cell)" loading="lazy"
                        class="accordion-panel__img" />
                    <div v-if="getCellTitle(cell)" class="accordion-panel__overlay">
                        <span class="accordion-panel__text">{{ getCellTitle(cell) }}</span>
                    </div>
                </div>
            </div>

            <!-- 移动端竖向手风琴（堆叠图片+自动轮播） -->
            <div class="accordion-panels accordion-panels--vertical" @mouseenter="pauseAutoPlay"
                @mouseleave="resumeAutoPlay" @touchstart.passive="pauseAutoPlay" @touchend.passive="resumeAutoPlay">
                <div v-for="(cell, idx) in props.item.fieldData"
                    :key="'v-' + String((cell as Record<string, unknown>)?.id ?? idx)" class="accordion-v-panel"
                    :class="{ 'accordion-v-panel--active': mobileActiveIndex === idx }"
                    @click="handleMobileClick(idx, cell)">
                    <img v-if="getCellImage(cell)" :src="getCellImage(cell)" :alt="getCellTitle(cell)" loading="lazy"
                        class="accordion-v-panel__img" />
                    <div v-if="getCellTitle(cell) && mobileActiveIndex === idx" class="accordion-v-panel__overlay">
                        <span class="accordion-v-panel__title">{{ getCellTitle(cell) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord, jumpToUrl } from './index.js'

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

const activeIndex = ref(0)
const mobileActiveIndex = ref(0)

let autoPlayTimer: ReturnType<typeof setInterval> | null = null
const AUTO_PLAY_INTERVAL = 3500
const isHovering = ref(false)

function getCellImage(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}
function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'title')
}
function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) {
        jumpToUrl(url)
    }
}

function handleDesktopEnter(idx: number): void {
    activeIndex.value = idx
}

function handleDesktopLeave(): void {
    // 保持最后悬停的项激活
}

function handleMobileClick(idx: number, cell: unknown): void {
    mobileActiveIndex.value = idx
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) {
        jumpToUrl(url)
    }
}

function startAutoPlay(): void {
    stopAutoPlay()
    autoPlayTimer = setInterval(() => {
        if (!isHovering.value && props.item.fieldData?.length > 1) {
            mobileActiveIndex.value = (mobileActiveIndex.value + 1) % props.item.fieldData.length
        }
    }, AUTO_PLAY_INTERVAL)
}

function stopAutoPlay(): void {
    if (autoPlayTimer) {
        clearInterval(autoPlayTimer)
        autoPlayTimer = null
    }
}

function pauseAutoPlay(): void {
    isHovering.value = true
}

function resumeAutoPlay(): void {
    isHovering.value = false
}

onMounted(() => {
    startAutoPlay()
})

onUnmounted(() => {
    stopAutoPlay()
})

watch(() => props.item.fieldData, () => {
    if (mobileActiveIndex.value >= (props.item.fieldData?.length || 0)) {
        mobileActiveIndex.value = 0
    }
})
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.accordion-section {
    width: 100%;
}

.accordion-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

/* ========== 桌面端横向手风琴 ========== */
.accordion-panels--horizontal {
    display: none;

    @media (min-width: $breakpoint-md) {
        display: flex;
        gap: 0.5rem;
        height: 340px;
        overflow: hidden;
    }
}

.accordion-panel {
    position: relative;
    flex: 1;
    min-width: 60px;
    border-radius: 0.75rem;
    overflow: hidden;
    cursor: pointer;
    transition: flex 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    @media (min-width: $breakpoint-md) {
        min-width: 80px;
        border-radius: 1rem;
        gap: 1rem;
    }

    &--active {
        flex: 5;
    }

    &__img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.5s ease;
    }

    &:hover .accordion-panel__img {
        transform: scale(1.05);
    }

    &__overlay {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 2rem 1.5rem;
        background: linear-gradient(to top, rgba(0, 0, 0, 0.65) 0%, rgba(0, 0, 0, 0) 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
    }

    &--active &__overlay {
        opacity: 1;
    }

    &__text {
        display: block;
        color: #fff;
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 1.4;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

/* ========== 移动端竖向手风琴（堆叠图片+自动轮播） ========== */
.accordion-panels--vertical {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    height: 360px;
    overflow: hidden;
    border-radius: 0.75rem;

    @media (min-width: $breakpoint-md) {
        display: none;
    }
}

.accordion-v-panel {
    position: relative;
    flex: 1;
    min-height: 50px;
    overflow: hidden;
    cursor: pointer;
    border-radius: 0.5rem;
    transition: flex 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    &--active {
        flex: 6;
    }

    &__img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 0.6s ease;
    }

    &--active &__img {
        transform: scale(1.02);
    }

    &__overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1.5rem;
        text-align: center;
        background: rgba(0, 0, 0, 0.3);
    }

    &__title {
        font-size: 1.5rem;
        font-weight: 700;
        color: #fff;
        line-height: 1.4;
        text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    }
}
</style>
